import classNames from 'classnames';
import React, { useCallback, useMemo, useState } from 'react';

import { InfoCard } from '@eika/card';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import type { PaymentLimits } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import {
    FormattedFuturePayment,
    FormattedFuturePayments,
    showPaymentLimitWarning,
    sumFuturePayments,
} from '../../utils/mapper/futurepaymentsUtil';
import { FuturePayment } from '../FuturePayment';
import FuturePaymentsListMonth from './FuturePaymentsListMonth';

import './FuturePaymentsList.scss';

export interface Props extends React.PropsWithChildren {
    formattedFuturePayments: FormattedFuturePayments[];
    availableBalance?: number;
    className?: string;
    showFromAccount?: boolean;
    paymentLimits?: PaymentLimits;
}

const FuturePaymentsList: React.FC<Props> = ({
    formattedFuturePayments,
    availableBalance,
    className,
    showFromAccount = false,
    children,
    paymentLimits,
}) => {
    const [futurePayment, setFuturePayment] = useState<FormattedFuturePayment>();
    const translated = useTranslation();

    const handleClick = useCallback((selectedPayment: FormattedFuturePayment) => {
        setFuturePayment(selectedPayment);
    }, []);

    const shouldShowPaymentLimitWarning = useMemo(
        () => showPaymentLimitWarning(formattedFuturePayments, paymentLimits),
        [formattedFuturePayments, paymentLimits],
    );

    return (
        <>
            <section className={classNames('future-payments-list__wrapper', className)}>
                {formattedFuturePayments.length > 0 ? (
                    <ul className="future-payments-list__container">
                        {formattedFuturePayments.map((formattedFuturePayment, index) => (
                            <FuturePaymentsListMonth
                                key={formattedFuturePayment.id}
                                label={formattedFuturePayment.label}
                                futurePayments={formattedFuturePayment.futurePayments}
                                onClick={handleClick}
                                availableBalance={index === 0 ? availableBalance : undefined}
                                totalAmountOut={sumFuturePayments(formattedFuturePayment.futurePayments, 'OUT')}
                                totalAmountIn={sumFuturePayments(formattedFuturePayment.futurePayments, 'IN')}
                                showFromAccount={showFromAccount}
                                showPaymentLimitWarning={shouldShowPaymentLimitWarning(
                                    formattedFuturePayment,
                                    index === 0,
                                )}
                            >
                                {index === 0 && children}
                            </FuturePaymentsListMonth>
                        ))}
                    </ul>
                ) : (
                    <InfoCard className="future-payments__empty" skipBackground>
                        <div className="future-payments__empty-filter future-payments-list-month__label-and-amount">
                            {children}
                        </div>
                        <RichIcon type={RichIconTypes.BAAT_M_SOL} />
                        <InfoCard.TitleH2>{translated.eFakturaHistory.noCreditors}</InfoCard.TitleH2>
                    </InfoCard>
                )}
            </section>
            {futurePayment && (
                <FuturePayment futurePayment={futurePayment} onClose={() => setFuturePayment(undefined)} />
            )}
        </>
    );
};

export default FuturePaymentsList;
